import React, { useEffect, useRef, useState } from 'react';
import ShiftDistanceChart from '../components/ShiftDistanceChart';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import dayjs from 'dayjs';
import { type IndividualShiftWiseDistanceData } from '../interface';
import EpirocMainPageHandler from 'handlers/epiroc.mainpage.handler';
import { setFilters } from 'store/epiroc.mainpage.slice';
import { useDispatch } from 'react-redux';
import DateRangeFilterBar from '../components/DateRangeFilterBar';

const DistanceAnalysis = () => {
  const epirocMainPageHandler = new EpirocMainPageHandler();
  const { theme } = useAppSelector((state) => state.authReducer.customer);
  const { accessToken } = useAppSelector((state) => state.authReducer);
  const { startDate, endDate } = useAppSelector(
    (state) => state.epirocMainPageReducer.filters
  );
  const enableDarkTheme = isDarkTheme(theme);

  const [
    loadingShiftWiseDistanceTravelled,
    setLoadingShiftWiseDistanceTravelled,
  ] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(
    // dayjs().startOf('day').subtract(2, 'days').toDate()
    // 1st march to 3rd march
    dayjs(startDate).toDate()
  );

  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs(endDate).toDate()
    // dayjs().endOf('day').toDate()
  );

  const dispatch = useDispatch();

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    dispatch(
      setFilters({
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      })
    );
  };

  const [shiftWiseDistanceTravelled, setShiftWiseDistanceTravelled] = useState<
    IndividualShiftWiseDistanceData[]
  >([]);

  const fetchShiftWiseDistanceTravelled = async () => {
    try {
      setLoadingShiftWiseDistanceTravelled(true);
      const cachedKey = `shift_wise_distance_${dayjs(selectedStartDate).format(
        'YYYY-MM-DD'
      )}_${dayjs(selectedEndDate).format('YYYY-MM-DD')}`;
      const cachedData = sessionStorage.getItem(cachedKey);
      if (cachedData && cachedData !== 'undefined') {
        setShiftWiseDistanceTravelled(JSON.parse(cachedData));
        setLoadingShiftWiseDistanceTravelled(false);
        return;
      }
      const response =
        await epirocMainPageHandler.getShiftWiseDistanceTravelled(
          dayjs(selectedStartDate).format('YYYY-MM-DD'),
          dayjs(selectedEndDate).format('YYYY-MM-DD')
        );
      setShiftWiseDistanceTravelled(response);
      sessionStorage.setItem(cachedKey, JSON.stringify(response));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadingShiftWiseDistanceTravelled(false);
    }
  };

  const handleFetchClick = () => {
    void fetchShiftWiseDistanceTravelled();
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      void fetchShiftWiseDistanceTravelled();
    }, 1000);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [accessToken]);

  return (
    <div className="w-full mt-2">
      <DateRangeFilterBar
        loading={loadingShiftWiseDistanceTravelled}
        onDateChange={handleDateChange}
        onFetch={handleFetchClick}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
      />
      {loadingShiftWiseDistanceTravelled && (
        <div
          className="w-full h-full flex justify-center items-center absolute z-10"
          style={{
            backgroundColor: enableDarkTheme
              ? '#3e3e3e62'
              : ThemePalette.light.contentBackground,
          }}
        >
          <SingleFadeLoader isFullScreen={false} />
        </div>
      )}
      <ShiftDistanceChart
        shiftWiseDistanceTravelled={shiftWiseDistanceTravelled}
      />
    </div>
  );
};

export default DistanceAnalysis;
